import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Copy } from "lucide-react";
import { SetuMark } from "@/components/Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/forgot-password`, { email });
      setResult(data);
      toast.success("Check your inbox — or use the link below.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const copyLink = () => {
    if (!result?.dev_reset_link) return;
    const full = window.location.origin + result.dev_reset_link;
    navigator.clipboard.writeText(full);
    toast.success("Link copied");
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-primary grid place-items-center text-primary-foreground"><SetuMark size={22} strokeWidth={2.6} /></div>
            <span className="font-display text-2xl tracking-tight">Setubuild.OS</span>
          </Link>
          <div className="flex items-center gap-2"><LanguageSwitcher /><ThemeToggle /></div>
        </div>
      </header>
      <main className="flex-1 grid place-items-center p-6">
        <form onSubmit={submit} className="w-full max-w-md space-y-4" data-testid="forgot-form">
          <h1 className="font-display text-4xl">Reset your password</h1>
          <p className="text-sm text-muted-foreground">Enter the email associated with your account. We'll send you a reset link (email delivery coming soon — for now the link appears here).</p>
          <div>
            <label className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Email</label>
            <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="forgot-email"
              className="w-full mt-1 border border-border bg-card px-3 py-2.5 text-sm focus:outline-none focus:border-primary" />
          </div>
          <button type="submit" disabled={busy} data-testid="forgot-submit"
            className="btn-sharp w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground py-3 disabled:opacity-50">
            {busy ? "…" : "Send reset link"} <ArrowRight className="h-4 w-4" />
          </button>

          {result?.dev_reset_link && (
            <div className="border border-primary/30 bg-primary/5 p-4 space-y-2" data-testid="forgot-devlink">
              <div className="mono text-xs break-all">{window.location.origin + result.dev_reset_link}</div>
              <div className="flex gap-2">
                <button type="button" onClick={copyLink} data-testid="forgot-copy"
                  className="btn-sharp inline-flex items-center gap-1.5 border border-border px-3 py-1.5 text-xs hover:bg-muted">
                  <Copy className="h-3 w-3" /> Copy
                </button>
                <Link to={result.dev_reset_link} data-testid="forgot-open"
                  className="btn-sharp inline-flex items-center gap-1.5 bg-primary text-primary-foreground px-3 py-1.5 text-xs">
                  Open reset page <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            </div>
          )}

          <div className="text-center text-sm text-muted-foreground">
            <Link to="/login" className="text-primary underline">Back to sign in</Link>
          </div>
        </form>
      </main>
    </div>
  );
}
